
const API_BASE = process.env.REACT_APP_API_BASE;

function getAuthToken() {
  return (
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    ""
  );
}

async function request(method, path, body) {
  const headers = { "Content-Type": "application/json" };
  const token = getAuthToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: (method === 'GET' || method === 'HEAD') ? undefined : (body ? JSON.stringify(body) : undefined),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed: ${res.status}`);
  }
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) return res.json();
  return res.text();
}





export const inventoryApi = {
  listItems() {
    return request("GET", "/inventory/items");
  },
  getItem(itemId) {
    return request("GET", `/inventory/items/${itemId}`);
  },
  createItem({ name, initialBatch }) {
    return request("POST", "/inventory/items", { name, initialBatch });
  },
  addBatch(itemId, batch) {
    return request("POST", `/inventory/items/${itemId}/batches`, batch);
  },
  updateBatch(itemId, batchId, updates) {
    return request("PATCH", `/inventory/items/${itemId}/batches/${batchId}`, updates);
  },
  updateItem(itemId, updates) {
    return request("PATCH", `/inventory/items/${itemId}`, updates);
  },
};

export const productApi = {
  list() {
    return request("GET", "/products");
  },
  create(payload) {
    return request("POST", "/products", payload);
  },
  update(productId, updates) {
    return request("PATCH", `/products/${productId}`, updates);
  },
  detail(productId) {
    return request("GET", `/products/${productId}`);
  },
  delete(productId) {
    return request("DELETE", `/products/${productId}`);
  },
};

export const ordersApi = {
  list(params) {
    if (params && (params.from || params.to || params.limit || params.accountId)) {
      const qs = new URLSearchParams();
      if (params.from) qs.set('from', params.from);
      if (params.to) qs.set('to', params.to);
      if (params.limit) qs.set('limit', String(params.limit));
      if (params.accountId) qs.set('accountId', params.accountId);
      return request("GET", `/orders?${qs.toString()}`);
    }
    return request("GET", "/orders");
  },
  preview(payload) {
    return request("POST", "/orders/preview", payload);
  },
  create(payload) {
    return request("POST", "/orders", payload);
  },
  update(orderId, updates) {
    return request("PATCH", `/orders/${orderId}`, updates);
  },
  detail(orderId) {
    return request("GET", `/orders/${orderId}`);
  },
  remove(orderId) {
    return request("DELETE", `/orders/${orderId}`);
  },
}; 

export const reportsApi = {
  summary(payload) {
    return request("POST", "/reports/summary", payload);
  },
};

// --- Auth API ---
export const authApi = {
  signup({ name, companyName, companyAddress, phone, email, password }) {
    return request("POST", "/signup", { name, companyName, companyAddress, phone, email, password });
  },
  verify({ phone }) {
    return request("POST", "/verify", { phone, verified: true });
  },
  login({ username, password, fcmToken }) {
    return request("POST", "/login", { username, password, fcmToken }).then((res) => {
      if (res?.token) localStorage.setItem("authToken", res.token);
      return res;
    });
  },
  logout() {
    localStorage.removeItem("authToken");
  },
};

export const accountsApi = {
  list() {
    return request("GET", "/money_acounts");
  },
  listOrgAccounts() {
    return request("GET", "/org-accounts");
  },
  create(payload) {
    return request("POST", "/money_acounts", payload);
  },
  update(accountId, updates) {
    return request("PATCH", `/money_acounts/${accountId}`, updates);
  },
  detail(accountId) {
    return request("GET", `/money_acounts/${accountId}`);
  },
  ledger(accountId, params) {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.limit) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return request("GET", `/money_acounts/${accountId}/ledger${q ? `?${q}` : ''}`);
  },
  transfer({ fromAccountId, toAccountId, amount, note }) {
    return request("POST", "/money_acounts/transfer", { fromAccountId, toAccountId, amount, note });
  },
  reconcile(accountId) {
    return request("POST", `/money_acounts/${accountId}/reconcile`);
  },
  listTransfers(limit) {
    const qs = limit ? `?limit=${Number(limit)}` : '';
    return request("GET", `/money_acounts/transfers${qs}`);
  },
  getTransfer(transferId) {
    return request("GET", `/money_acounts/transfers/${transferId}`);
  },
  transfer({ fromAccountId, toAccountId, amount, note }) {
    return request("POST", "/money_acounts/transfer", { fromAccountId, toAccountId, amount, note });
  },
  deleteTransfer(transferId) {
    return request("DELETE", `/money_acounts/transfers/${transferId}`);
  },
  deleteLedgerEntry(entryId) {
    return request("DELETE", `/money_acounts/ledger/${entryId}`);
  },
  updateLedgerEntry(entryId, { amount, date }) {
    return request("PATCH", `/money_acounts/ledger/${entryId}`, { amount, date });
  },
};

export const userApi = {
  me() {
    return request("GET", "/me");
  },
  subscriptionStatus() {
    return request("GET", "/subscription/status");
  },
  activateSubscription(plan = "annual") {
    return request("POST", "/subscription/activate", { plan });
  },
  saveFcmToken(token) {
    return request("POST", "/me/fcm", { token });
  },
  updateSelectedAccount(accountId) {
    return request("POST", "/update-selected-account", { accountId });
  },
  getUserPermissions(accountId) {
    return request("GET", `/user/permissions?accountId=${accountId}`);
  },
  
  // User management for org admins
  getOrgUsers() {
    return request("GET", "/org/users");
  },
  createUser(userData) {
    return request("POST", "/org/users", userData);
  },
  updateUser(userId, userData) {
    return request("PUT", `/org/users/${userId}`, userData);
  },
  deleteUser(userId) {
    return request("DELETE", `/org/users/${userId}`);
  },
};

export const orgApi = {
  get() {
    return request("GET", "/org");
  },
  update({ name, logoDataUrl, address, phone, email }) {
    return request("PATCH", "/org", { name, logoDataUrl, address, phone, email });
  },
};

export const adminApi = {
  listUsers() {
    return request("GET", "/admin/users");
  },
  activateUser(userId) {
    return request("POST", `/admin/users/${userId}/activate`);
  },
  deactivateUser(userId) {
    return request("POST", `/admin/users/${userId}/deactivate`);
  },
  updateSubscription(userId, { plan, months, status, endsAt, active }) {
    return request("POST", `/admin/users/${userId}/subscription`, { plan, months, status, endsAt, active });
  },
  notify({ tokens, title, body, data }) {
    return request("POST", "/admin/notify", { tokens, title, body, data });
  },
  getWhatsAppUsage(queryString = '') {
    return request("GET", `/admin/whatsapp/usage${queryString ? '?' + queryString : ''}`);
  },
  getWhatsAppUsers() {
    return request("GET", "/admin/whatsapp/users");
  },
  deleteUser(userId) {
    return request("DELETE", `/admin/users/${userId}`);
  },
  getUserDeletePreview(userId) {
    return request("GET", `/admin/users/${userId}/delete-preview`);
  },
  getQuotaConfig() {
    return request("GET", "/admin/quota/config");
  },
  updateQuotaConfig(config) {
    return request("POST", "/admin/quota/config", config);
  },
  getUserQuota(userId) {
    return request("GET", `/admin/users/${userId}/quota`);
  },
  setUserQuotaOverride(userId, override) {
    return request("POST", `/admin/users/${userId}/quota`, override);
  },
  removeUserQuotaOverride(userId) {
    return request("DELETE", `/admin/users/${userId}/quota`);
  },
  getSubscriptionPlans() {
    return request("GET", "/admin/subscription-plans");
  },
};

export const expensesApi = {
  list(params) {
    if (params && (params.category || params.from || params.to || params.limit || params.accountId)) {
      const qs = new URLSearchParams();
      if (params.category) qs.set('category', params.category);
      if (params.from) qs.set('from', params.from);
      if (params.to) qs.set('to', params.to);
      if (params.limit) qs.set('limit', String(params.limit));
      if (params.accountId) qs.set('accountId', params.accountId);
      return request("GET", `/expenses?${qs.toString()}`);
    }
    return request("GET", "/expenses");
  },
  detail(expenseId) {
    return request("GET", `/expenses/${expenseId}`);
  },
  create(payload) {
    return request("POST", "/expenses", payload);
  },
  update(expenseId, updates) {
    return request("PATCH", `/expenses/${expenseId}`, updates);
  },
  remove(expenseId) {
    return request("DELETE", `/expenses/${expenseId}`);
  },
  listCategories() {
    // Unified categories collection; type required for page context
    const qs = new URLSearchParams();
    qs.set('type', 'expense');
    return request("GET", `/categories?${qs.toString()}`);
  },
  createCategory(name) {
    return request("POST", "/categories", { name, type: 'expense' });
  },
  // Unified categories
  listUnified(type='expense') {
    const qs = new URLSearchParams();
    if (type) qs.set('type', type);
    const q = qs.toString();
    return request("GET", `/categories${q?`?${q}`:''}`);
  },
  createUnified({ name, type='expense' }) {
    return request("POST", "/categories", { name, type });
  },
  listUnifiedCustom(type='expense') {
    const qs = new URLSearchParams();
    if (type) qs.set('type', type);
    const q = qs.toString();
    return request("GET", `/categories/custom${q?`?${q}`:''}`);
  },
  updateUnified(categoryId, name) {
    return request("PATCH", `/categories/${categoryId}`, { name });
  },
  listIncomeCategories() {
    return request("GET", "/income/categories");
  },
  createIncomeCategory(name) {
    return request("POST", "/income/categories", { name });
  },
  listCustomExpenseCategories() {
    return request("GET", "/expenses/categories/custom");
  },
  listCustomIncomeCategories() {
    return request("GET", "/income/categories/custom");
  },
  updateExpenseCategory(categoryId, name) {
    return request("PATCH", `/expenses/categories/${categoryId}`, { name });
  },
  updateIncomeCategory(categoryId, name) {
    return request("PATCH", `/income/categories/${categoryId}`, { name });
  },
};

export const customersApi = {
  list() {
    return request("GET", "/customers");
  },
  create({ name, phone, address, customerNumber, email }) {
    return request("POST", "/customers", { name, phone, address, customerNumber, email });
  },
  update(customerId, updates) {
    return request("PATCH", `/customers/${customerId}`, updates);
  },
  remove(customerId) {
    return request("DELETE", `/customers/${customerId}`);
  },
};

export const businessAccountsApi = {
  list() {
    return request("GET", "/org-accounts");
  },
  create({ name, description }) {
    return request("POST", "/org-accounts", { name, description });
  },
  update(accountId, updates) {
    return request("PATCH", `/org-accounts/${accountId}`, updates);
  },
  remove(accountId) {
    return request("DELETE", `/org-accounts/${accountId}`);
  },
};

export const razorpayApi = {
  createOrder(planId, userId, userEmail) {
    return request("POST", "/razorpay/create-order", { planId, userId, userEmail });
  },
  verifyPayment(paymentData) {
    return request("POST", "/razorpay/verify-payment", paymentData);
  },
  getSubscriptionStatus() {
    return request("GET", "/subscription/status");
  },
  getSubscriptionPlans() {
    return request("GET", "/razorpay/subscription-plans");
  },
  upsertSubscriptionPlan(planData) {
    return request("POST", "/razorpay/admin/subscription-plans", { planData });
  },
  deleteSubscriptionPlan(planId) {
    return request("DELETE", `/razorpay/admin/subscription-plans/${planId}`);
  },
};

export const whatsappApi = {
  sendInvoice(customerPhone, invoiceData, pdfUrl) {
    return request("POST", "/whatsapp/send-invoice", { customerPhone, invoiceData, pdfUrl });
  },
};

export const printerApi = {
  registerHost(payload) {
    return request("POST", "/printer/hosts/register", payload);
  },
  listHosts(includeOffline = false) {
    const qs = includeOffline ? '?includeOffline=true' : '';
    return request("GET", `/printer/hosts${qs}`);
  },
  removeHost(deviceId) {
    return request("DELETE", `/printer/hosts/${deviceId}`);
  },
  enqueueJob(payload) {
    return request("POST", "/printer/jobs", payload);
  },
  getJob(jobId) {
    return request("GET", `/printer/jobs/${jobId}`);
  },
  listJobsForHost(deviceId, status = 'pending') {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return request("GET", `/printer/jobs/host/${deviceId}${qs}`);
  },
  updateJobStatus(jobId, payload) {
    return request("POST", `/printer/jobs/${jobId}/status`, payload);
  },
  createFirebaseToken() {
    return request("GET", "/printer/firebase-token");
  },
};